import { TradeCard } from 'store/services/trade/tradeApi';

/**
 * Compute condensed "Sets Accepted" summary for a TradeCard.
 * - No sets specified → "Any Set"
 * - 1-3 sets → comma-separated codes ("FDN, MKM, DSK")
 * - 4+ sets → first 2 codes + "+ N more"
 */
export function getSetsAcceptedSummary(card: TradeCard): string {
  const details = card.acceptableSetDetails;
  const sets = card.acceptableSets;

  // If no set restrictions, it's "Any Set"
  if ((!details || details.length === 0) && (!sets || sets.length === 0)) {
    return 'Any Set';
  }

  // Prefer acceptableSetDetails (has codes), fall back to acceptableSets (names)
  if (details && details.length > 0) {
    const codes = details.map(d => d.code.toUpperCase());
    if (codes.length <= 3) {
      return codes.join(', ');
    }
    return `${codes[0]}, ${codes[1]} + ${codes.length - 2} more`;
  }

  // Fallback to acceptableSets (string names)
  if (sets && sets.length > 0) {
    if (sets.length <= 3) {
      return sets.join(', ');
    }
    return `${sets.length} sets`;
  }

  return 'Any Set';
}

/**
 * Get the count of accepted sets for a card.
 */
export function getSetsAcceptedCount(card: TradeCard): number {
  if (card.acceptableSetDetails && card.acceptableSetDetails.length > 0) {
    return card.acceptableSetDetails.length;
  }
  if (card.acceptableSets && card.acceptableSets.length > 0) {
    return card.acceptableSets.length;
  }
  return 0; // 0 means "Any Set"
}

/**
 * Compute finish summary from foil acceptance booleans.
 */
export function getFinishSummary(card: TradeCard): string {
  const foil = card.acceptFoil;
  const nonFoil = card.acceptNonFoil;

  if (foil && nonFoil) return 'Foil / Nonfoil';
  if (foil) return 'Foil';
  if (nonFoil) return 'Nonfoil';
  // Fallback — shouldn't happen but handle gracefully
  return 'Any';
}

const CONDITION_ABBREVIATIONS: Record<string, string> = {
  NEAR_MINT: 'NM',
  SLIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
};

// Display order for conditions (best to worst)
const CONDITION_ORDER = ['NEAR_MINT', 'SLIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED'];

/**
 * Compute condition summary from acceptableConditions array.
 * Lists accepted conditions as abbreviations (e.g., "NM, LP, MP").
 */
export function getConditionSummary(card: TradeCard): string {
  const conditions = card.acceptableConditions;

  if (!conditions || conditions.length === 0) {
    return 'Any';
  }

  const condSet = new Set(conditions.map(c => c.toUpperCase()));

  // If all 5 conditions are accepted, show "Any"
  if (condSet.size >= CONDITION_ORDER.length) {
    return 'Any';
  }

  // List abbreviations in grading order
  return CONDITION_ORDER
    .filter(c => condSet.has(c))
    .map(c => CONDITION_ABBREVIATIONS[c] || c)
    .join(', ');
}

const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  en: 'English',
  ja: 'Japanese',
  ko: 'Korean',
  zhs: 'Chinese (S)',
  zht: 'Chinese (T)',
  de: 'German',
  fr: 'French',
  it: 'Italian',
  pt: 'Portuguese',
  es: 'Spanish',
  ru: 'Russian',
};

/**
 * Compute language summary from acceptableLanguages array.
 */
export function getLanguageSummary(card: TradeCard): string {
  const languages = card.acceptableLanguages;

  if (!languages || languages.length === 0) {
    return 'Any';
  }

  if (languages.length === 1) {
    return LANGUAGE_DISPLAY_NAMES[languages[0].toLowerCase()] || languages[0];
  }

  if (languages.length <= 3) {
    return languages
      .map(l => LANGUAGE_DISPLAY_NAMES[l.toLowerCase()] || l)
      .join(', ');
  }

  return `${languages.length} languages`;
}

/**
 * Get display price for a card (display only — backend recalculates on trade).
 */
export function getCardDisplayPrice(card: TradeCard): number {
  return card.pointValue;
}

/**
 * Calculate total display value for a list of cards.
 */
export function calculateTotalValue(cards: TradeCard[]): number {
  return cards.reduce((sum, card) => {
    return sum + (card.pointValue * card.quantity);
  }, 0);
}
